/**
 * INP com o NOME do culpado (CHAT002-4520).
 *
 * O PostHog mede o INP (`$web_vitals`), mas o evento chega sem atribuição: o
 * `entries` da interação é serializado vazio e não há alvo, tipo, nem a quebra
 * entre espera, processamento e pintura. Foi por isso que o card nasceu com
 * "não sei qual é a causa; INP alto é sintoma" — 1.692 ms de média na conversa
 * com agente, e nenhuma pista de QUAL clique.
 *
 * O Next compila o `web-vitals` com atribuição (`experimental.webVitalsAttribution`);
 * esta função transforma a métrica num evento pequeno e sem dado pessoal: a
 * rota (sem query), o seletor CSS do alvo (nunca o texto), o tipo da
 * interação, as três fases e o script mais longo do quadro (origem sem query,
 * função, quanto durou). Com isso cada INP ruim em produção vira diagnóstico,
 * em vez de pedir um profile a alguém.
 *
 * Amostragem: tudo a partir de 500 ms ("ruim"); 25% entre 200 e 500 ms
 * ("precisa melhorar"); nada abaixo — o volume fica perto do que o `$web_vitals`
 * já gera, e o que importa entra inteiro.
 */

export const LIMIAR_INP_RUIM_MS = 500;
export const LIMIAR_INP_ATENCAO_MS = 200;
export const FRACAO_AMOSTRA_ATENCAO = 0.25;

type ScriptLoaf = {
	sourceURL?: string;
	sourceFunctionName?: string;
	invoker?: string;
	duration?: number;
};
type EntradaLoaf = { duration?: number; scripts?: ScriptLoaf[] };

type AtribuicaoInp = {
	interactionTarget?: string;
	interactionType?: string;
	inputDelay?: number;
	processingDuration?: number;
	presentationDelay?: number;
	loadState?: string;
	longAnimationFrameEntries?: EntradaLoaf[];
};

export type MetricaWebVitals = {
	name: string;
	value: number;
	rating?: string;
	attribution?: AtribuicaoInp;
};

export type EventoInpAtribuido = {
	valor_ms: number;
	classificacao: string | null;
	rota: string;
	alvo: string | null;
	tipo: string | null;
	espera_ms: number | null;
	processamento_ms: number | null;
	pintura_ms: number | null;
	estado_carga: string | null;
	script_origem: string | null;
	script_funcao: string | null;
	script_invocador: string | null;
	script_ms: number | null;
};

const arred = (n: unknown) =>
	typeof n === "number" && Number.isFinite(n) ? Math.round(n) : null;

/** Só o caminho do arquivo do script, sem host de CDN nem query. */
function origemDoScript(url?: string): string | null {
	if (!url) return null;
	try {
		const u = new URL(url);
		return u.pathname.slice(-160);
	} catch {
		return url.split("?")[0].slice(-160);
	}
}

export function resumirInp(
	metrica: MetricaWebVitals,
	caminho: string,
	sorteio: number
): EventoInpAtribuido | null {
	if (metrica.name !== "INP") return null;
	const valor = metrica.value;
	if (!(valor >= LIMIAR_INP_ATENCAO_MS)) return null;
	if (valor < LIMIAR_INP_RUIM_MS && sorteio >= FRACAO_AMOSTRA_ATENCAO)
		return null;

	const a = metrica.attribution ?? {};
	let maisLongo: ScriptLoaf | null = null;
	for (const quadro of a.longAnimationFrameEntries ?? []) {
		for (const script of quadro.scripts ?? []) {
			if (!maisLongo || (script.duration ?? 0) > (maisLongo.duration ?? 0))
				maisLongo = script;
		}
	}

	return {
		valor_ms: Math.round(valor),
		classificacao: metrica.rating ?? null,
		rota: caminho.split("?")[0],
		alvo: a.interactionTarget ? a.interactionTarget.slice(0, 160) : null,
		tipo: a.interactionType ?? null,
		espera_ms: arred(a.inputDelay),
		processamento_ms: arred(a.processingDuration),
		pintura_ms: arred(a.presentationDelay),
		estado_carga: a.loadState ?? null,
		script_origem: origemDoScript(maisLongo?.sourceURL),
		script_funcao: maisLongo?.sourceFunctionName
			? maisLongo.sourceFunctionName.slice(0, 120)
			: null,
		script_invocador: maisLongo?.invoker
			? maisLongo.invoker.slice(0, 120)
			: null,
		script_ms: arred(maisLongo?.duration),
	};
}
